import { Button, Card } from '@parrot-software/design-system';
import { TriangleAlert } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

export type ErrorRetryCardProps = {
  /** Label of the retry button. */
  actionLabel: string;
  body: string;
  className?: string;
  /** Runs the call that failed again, so the user carries on from where they were. */
  onRetry: () => void;
  title: string;
};

/**
 * The error state of a screen that lives inside the application frame: what went wrong, and a button
 * that runs the same action again.
 *
 * It replaces the content of the screen and not the screen itself, so the heading the user was
 * reading stays where it was and a retry never costs them a navigation.
 *
 * The texts arrive already translated and the retry arrives as a callback, so each screen words its
 * own failure and decides which call it repeats.
 *
 * @param {ErrorRetryCardProps} props - Component properties
 */
export const ErrorRetryCard = ({ actionLabel, body, className, onRetry, title }: ErrorRetryCardProps) => (
  <Card
    className={cn(
      'flex w-full flex-col items-center gap-6 border-0 bg-transparent p-0 text-center font-sans shadow-none',
      'md:border md:bg-card md:p-10 md:shadow-xs',
      className,
    )}
    data-testid="identity-error-retry-card"
  >
    <div className="flex max-w-[420px] flex-col items-center gap-3">
      <TriangleAlert aria-hidden className="size-9 text-destructive" strokeWidth={1.5} />
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
    <Button className="w-full sm:w-fit" onClick={onRetry} type="button">
      {actionLabel}
    </Button>
  </Card>
);
